import apiClient from "./client";

export async function getDashboard() {
  return apiClient.get("/admin/dashboard");
}

export async function getAdminStats(days = 30) {
  return apiClient.get(`/admin/stats?days=${days}`);
}

export async function getAdminPageStats(days = 30) {
  return apiClient.get(`/admin/page-stats?days=${days}`);
}

export async function getAdminServerStats(days = 30) {
  return apiClient.get(`/admin/server-stats?days=${days}`);
}

export async function getAdminStoreStats(period = "30d") {
  return apiClient.get(`/admin/store-stats?period=${encodeURIComponent(period)}`);
}

export async function getAdminPlayerStats({ search, linked, synced, page = 1, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (linked && linked !== "ALL") params.set("linked", linked);
  if (synced && synced !== "ALL") params.set("synced", synced);
  params.set("page", String(page));
  params.set("limit", String(limit));
  return apiClient.get(`/admin/player-stats?${params.toString()}`);
}

export async function getAdminPlayerStatsDetail(uuid) {
  return apiClient.get(`/admin/player-stats/${encodeURIComponent(uuid)}`);
}

export async function getUsers({
  search,
  linked,
  role,
  isBanned,
  isMuted,
  page = 1,
  limit = 50,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (linked && linked !== "ALL") params.set("linked", linked);
  if (role && role !== "ALL") params.set("role", role);
  if (isBanned != null && isBanned !== "ALL") params.set("isBanned", String(isBanned));
  if (isMuted != null && isMuted !== "ALL") params.set("isMuted", String(isMuted));
  params.set("page", String(page));
  params.set("limit", String(limit));
  return apiClient.get(`/admin/users?${params.toString()}`);
}

export async function updateUserRole(id, { role, secondaryRoles } = {}) {
  const body = { role };
  if (secondaryRoles !== undefined) body.secondaryRoles = secondaryRoles;
  return apiClient.patch(`/admin/users/${id}/role`, body);
}

export async function getUserGroups(id) {
  return apiClient.get(`/admin/users/${id}/groups`);
}

export async function updateUserGroups(id, groupSlugs) {
  return apiClient.put(`/admin/users/${id}/groups`, { groupSlugs });
}

export async function getGroups() {
  return apiClient.get("/admin/groups");
}

export async function updateUserBan(id, isBanned) {
  return apiClient.patch(`/admin/users/${id}/ban`, { isBanned });
}

export async function updateUserMute(id, isMuted) {
  return apiClient.patch(`/admin/users/${id}/mute`, { isMuted });
}

export async function getUserProfile(id) {
  return apiClient.get(`/admin/users/${id}`);
}

export async function regenerateUserStaticWebUsername(id) {
  return apiClient.post(`/admin/users/${id}/regenerate-static-web-username`, {});
}

/** Requires `admin.users.manage`. Forces Mojang fetch + local head PNG regeneration. */
export async function refreshUserMinecraftHead(id) {
  return apiClient.post(`/admin/users/${id}/refresh-minecraft-head`, {});
}

export async function adjustUserStoreCredit(id, { deltaCents, note } = {}) {
  return apiClient.post(`/admin/users/${id}/store-credit`, { deltaCents, note });
}

export async function getPermissions() {
  return apiClient.get("/admin/permissions");
}

export async function updatePermissions(matrix) {
  return apiClient.patch("/admin/permissions", { matrix });
}

export async function getForumBoardsAdmin() {
  return apiClient.get("/admin/forum/boards");
}

export async function createForumBoardAdmin(data) {
  return apiClient.post("/admin/forum/boards", data);
}

export async function patchForumBoardAdmin(boardId, data) {
  return apiClient.patch(`/admin/forum/boards/${boardId}`, data);
}

export async function getForumBoardPermissions(boardId) {
  return apiClient.get(`/admin/forum/boards/${boardId}/permissions`);
}

export async function updateForumBoardPermissions(boardId, permissions) {
  return apiClient.put(`/admin/forum/boards/${boardId}/permissions`, { permissions });
}

/**
 * Pagination + filters query params forwarded to `/admin/audit/history`.
 * @param {Record<string,string|number>} [params]
 */
export async function getAuditHistory(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  });
  const qs = q.toString();
  return apiClient.get(`/admin/audit/history${qs ? `?${qs}` : ""}`);
}

export async function getDmReports(params = {}) {
  const q = new URLSearchParams();
  if (params.page != null && params.page !== "") q.set("page", String(params.page));
  if (params.limit != null && params.limit !== "") q.set("limit", String(params.limit));
  if (params.status && params.status !== "ALL") q.set("status", String(params.status));
  const qs = q.toString();
  return apiClient.get(`/admin/dm-reports${qs ? `?${qs}` : ""}`);
}

export async function patchDmReportResolved(reportId) {
  return apiClient.patch(`/admin/dm-reports/${reportId}`, { status: "RESOLVED" });
}

export async function getPlayerReports({
  status,
  kind,
  search,
  reporterPlayerId,
  subjectPlayerId,
  page = 1,
  limit = 25,
} = {}) {
  const q = new URLSearchParams();
  if (status) q.set("status", String(status));
  if (kind && kind !== "ALL") q.set("kind", String(kind));
  if (search) q.set("search", String(search));
  if (reporterPlayerId) q.set("reporterPlayerId", String(reporterPlayerId));
  if (subjectPlayerId) q.set("subjectPlayerId", String(subjectPlayerId));
  q.set("page", String(page));
  q.set("limit", String(limit));
  return apiClient.get(`/admin/reports?${q.toString()}`);
}

export async function getPlayerReport(id) {
  return apiClient.get(`/admin/reports/${id}`);
}

export async function patchPlayerReportStatus(id, status) {
  return apiClient.patch(`/admin/reports/${id}`, { status });
}

export async function getModerationPlayer(id) {
  return apiClient.get(`/admin/moderation/players/${encodeURIComponent(id)}`);
}

export async function addModerationPlayerNote(id, body) {
  return apiClient.post(`/admin/moderation/players/${encodeURIComponent(id)}/notes`, { body });
}
